import React, { useEffect, useRef, useState } from 'react';

import { modalOptions } from 'Helpers/modalOptions';

import Alert from 'CommonComponents/Alert';
import FormInput from 'CommonComponents/FormInput';
import Modal from 'CommonComponents/Modal';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';
import SelectInput from 'CommonComponents/SelectInput';
import TextButton from 'CommonComponents/TextButton';

import useModal from 'Hooks/useModal';

import TrashIcon from 'Assets/icons/trash.svg';

/**
 * SportAllocationModal — a modal to assign (or re-assign) sport tags to a budget line.
 *
 * The modal lets the user pick one or more sports and specify a percentage for each.
 * Percentages must sum to 100.
 *
 * Mounted on demand by EventTagCell: it shows itself on mount and calls onClose()
 * once the flowbite hide transition has completed, so the parent can unmount it.
 */
export default function SportAllocationModal({ line, storeUrl, sports, currentAllocations, onSaved, onClose, venueId = null }) {
    const modal = useModal({ options: { ...modalOptions(false), onHide: onClose }, visible: true });

    // Flowbite appends its backdrop outside the React tree, so unmounting while
    // the modal is still shown (e.g. the parent table refetches and remounts its
    // rows after a save) would leave the overlay behind — force-hide on unmount.
    const modalStateRef = useRef(modal);
    modalStateRef.current = modal;

    useEffect(
        () => () => {
            const { element } = modalStateRef.current;
            if (element && element.isVisible()) {
                element.hide();
            }
        },
        [],
    );

    const parseSportId = (val) => {
        if (val == null || val === '') return '';
        const cleaned = String(val).replace(/,/g, '');
        const num = parseInt(cleaned, 10);
        return isNaN(num) ? '' : String(num);
    };

    const buildRows = () =>
        currentAllocations.length > 0
            ? currentAllocations.map((a) => ({
                  sport_id: parseSportId(a.sport_id),
                  percentage: a.percentage,
              }))
            : [{ sport_id: '', percentage: '' }];

    const [rows, setRows] = useState(buildRows);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    const close = () => modal.hide();

    const addRow = () => {
        setRows((prev) => [...prev, { sport_id: '', percentage: '' }]);
    };

    const removeRow = (index) => {
        setRows((prev) => {
            const filtered = prev.filter((_, i) => i !== index);
            if (filtered.length === 1 && filtered[0].sport_id) {
                filtered[0].percentage = 100;
            }
            return filtered;
        });
    };

    const updateRow = (index, field, value) => {
        setRows((prev) => {
            const updated = prev.map((row, i) => (i === index ? { ...row, [field]: value } : row));
            if (updated.length === 1 && field === 'sport_id' && value) {
                updated[0].percentage = 100;
            }
            return updated;
        });
    };

    const totalPct = rows.reduce((sum, r) => sum + parseFloat(r.percentage || 0), 0);
    const displayTotalPct = parseFloat(totalPct.toFixed(2));

    const validate = () => {
        const errs = {};
        if (rows.length === 0) {
            errs.general = $t('At least one event tag is required.');
        }
        rows.forEach((row, i) => {
            if (!row.sport_id) {
                errs[`row_${i}_sport`] = $t('Select an event.');
            }
            const pct = parseFloat(row.percentage);
            if (isNaN(pct) || pct <= 0 || pct > 100) {
                errs[`row_${i}_pct`] = $t('Percentage must be between 0.01 and 100.');
            }
        });
        if (Object.keys(errs).length === 0 && Math.abs(totalPct - 100) > 0.01) {
            errs.total = $t('Percentages must sum to 100. Current total: {{total}}%', { total: displayTotalPct });
        }
        return errs;
    };

    const handleSave = () => {
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }

        setErrors({});
        setSaving(true);

        const url = storeUrl.replace('__TYPE__', line.type).replace('__ID__', line.id);

        axios
            .post(url, {
                allocations: rows.map((r) => ({
                    sport_id: parseInt(r.sport_id, 10),
                    percentage: parseFloat(r.percentage),
                })),
                ...(venueId ? { venue_id: venueId } : {}),
            })
            .then(({ data }) => {
                const saved = (data.data || data).map((a) => ({
                    sport_id: a.sport_id,
                    sport_name: a.sport_name,
                    percentage: a.percentage,
                }));
                // Hide before notifying the parent — onSaved triggers a table
                // refetch that remounts the rows hosting this modal.
                close();
                onSaved(saved);
            })
            .catch((err) => {
                const serverErrors = err?.response?.data?.errors;
                if (serverErrors) {
                    setErrors({ general: Object.values(serverErrors).flat().join(' ') });
                } else {
                    setErrors({ general: $t('Failed to save event tags. Please try again.') });
                }
            })
            .finally(() => setSaving(false));
    };

    const usedSportIds = rows.map((r) => r.sport_id).filter(Boolean);

    return (
        <Modal id={`sport-allocation-modal-${line.type}-${line.id}`} modal={modal}>
            <ModalHeader title={$t('Assign Event Tags')} close={close} />

            <div className="modal__form-container">
                {errors.general && <Alert type="error">{errors.general}</Alert>}

                {rows.map((row, i) => (
                    <div key={i} className="tw-flex tw-items-start tw-gap-2">
                        <div className="tw-flex-1">
                            <SelectInput
                                value={row.sport_id}
                                onChange={(e) => updateRow(i, 'sport_id', e.target.value)}
                                showError={!!errors[`row_${i}_sport`]}
                                errorText={errors[`row_${i}_sport`]}
                            >
                                <option value="">{$t('Select event...')}</option>
                                {sports.map((s) => (
                                    <option key={s.id} value={String(s.id)} disabled={usedSportIds.includes(String(s.id)) && row.sport_id !== String(s.id)}>
                                        {s.name}
                                    </option>
                                ))}
                            </SelectInput>
                        </div>

                        <div className="tw-w-24">
                            <FormInput
                                hideLabel
                                type="number"
                                min="0.01"
                                max="100"
                                step="0.01"
                                placeholder="%"
                                value={row.percentage}
                                onChange={(e) => updateRow(i, 'percentage', e.target.value)}
                                readOnly={rows.length === 1}
                                showError={!!errors[`row_${i}_pct`]}
                                errorText={errors[`row_${i}_pct`]}
                            />
                        </div>

                        {rows.length > 1 && (
                            <TextButton variant="danger" onClick={() => removeRow(i)} className="tw-py-2.5" aria-label={$t('Remove')}>
                                <TrashIcon className="tw-w-3.5 tw-h-3.5" />
                            </TextButton>
                        )}
                    </div>
                ))}

                {errors.total && <p className="modal__input-error-text">{errors.total}</p>}

                <div className="tw-flex tw-items-center tw-justify-between">
                    <TextButton variant="primary" onClick={addRow}>
                        {$t('+ Add event')}
                    </TextButton>
                    <span className={`tw-text-sm ${Math.abs(totalPct - 100) < 0.01 ? 'tw-text-green' : 'tw-text-charcoal-50'}`}>
                        {$t('Total: {{total}}%', { total: displayTotalPct })}
                    </span>
                </div>

                <ModalFooter
                    cancelButtonText={$t('Cancel')}
                    primaryButtonText={$t('Save')}
                    submitButtonVariant="primary"
                    onCancel={close}
                    handleSubmit={handleSave}
                    isSubmitting={saving}
                />
            </div>
        </Modal>
    );
}
